import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

interface TechItem {
  icon: string;
  label: string;
}

interface Step {
  number: string;
  title: string;
  description: string;
}

interface ServicePageLayoutProps {
  tag: string;
  title: string;
  subtitle: string;
  heroImage: string;
  heroAlt: string;
  intro: string;
  techItems: TechItem[];
  steps: Step[];
  advantages: string[];
  relatedServices: { label: string; href: string }[];
  galleryImages: { src: string; alt: string }[];
}

export default function ServicePageLayout({
  tag,
  title,
  subtitle,
  heroImage,
  heroAlt,
  intro,
  techItems,
  steps,
  advantages,
  relatedServices,
  galleryImages,
}: ServicePageLayoutProps) {
  return (
    <div className="min-h-screen bg-white">
    <Navbar />
    <main className="pt-20 bg-white">
      {/* Hero */}
      <section className="relative h-[420px] md:h-[560px] flex items-end overflow-hidden">
        <img
          src={heroImage}
          alt={heroAlt}
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-black/20"></div>
        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 md:px-8 pb-12 md:pb-16">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-green-300 text-xs font-semibold mb-4 hover:text-white transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i>
            Retour à l&apos;accueil
          </Link>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[#00a340]/60 bg-[#00a340]/15 mb-4">
            <span className="w-1.5 h-1.5 rounded-full bg-[#4cd97a] inline-block"></span>
            <span className="text-[#a8f0c0] text-xs font-bold uppercase tracking-widest">{tag}</span>
          </div>
          <h1
            className="text-3xl md:text-6xl font-extrabold text-white leading-tight mb-3"
            style={{ fontFamily: "'Montserrat', sans-serif" }}
          >
            {title}
          </h1>
          <p className="text-gray-200 text-sm md:text-lg max-w-xl">{subtitle}</p>
        </div>
      </section>

      {/* Intro + Tech */}
      <section className="py-14 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="text-xs font-bold uppercase tracking-widest text-[#00a340] mb-2 block">Notre expertise</span>
            <h2
              className="text-2xl md:text-4xl font-extrabold text-gray-900 mb-5 leading-tight"
              style={{ fontFamily: "'Montserrat', sans-serif" }}
            >
              Une solution{" "}
              <span className="italic text-[#00a340]">sur mesure</span>
            </h2>
            <p className="text-gray-600 text-sm md:text-base leading-relaxed mb-8">{intro}</p>
            <a
              href="/#contact"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-[#00a340] text-white font-semibold text-sm whitespace-nowrap hover:bg-[#007a30] transition-colors cursor-pointer"
            >
              <i className="ri-file-list-3-line"></i>
              Demander un devis gratuit
            </a>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {techItems.map((item) => (
              <div
                key={item.label}
                className="flex items-center gap-3 p-4 rounded-xl border border-gray-100 bg-gray-50 hover:border-[#00a340]/40 hover:bg-[#f0fdf4] transition-colors"
              >
                <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-[#00a340]/10 text-[#00a340] flex-shrink-0">
                  <i className={`${item.icon} text-lg`}></i>
                </div>
                <span className="text-gray-800 text-sm font-semibold leading-tight">{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery */}
      {galleryImages.length > 0 && (
        <section className="pb-14 md:pb-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {galleryImages.map((img, i) => (
                <div
                  key={i}
                  className="rounded-xl overflow-hidden"
                  style={{ height: "220px" }}
                >
                  <img
                    src={img.src}
                    alt={img.alt}
                    className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-500"
                  />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Process */}
      <section className="py-14 md:py-20 bg-[#f8fdf9]">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-10 md:mb-14">
            <span className="text-xs font-bold uppercase tracking-widest text-[#00a340] mb-2 block">Notre méthode</span>
            <h2
              className="text-2xl md:text-4xl font-extrabold text-gray-900"
              style={{ fontFamily: "'Montserrat', sans-serif" }}
            >
              Comment nous intervenons
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {steps.map((step) => (
              <div key={step.number} className="relative">
                <div className="text-5xl font-black text-[#00a340]/10 mb-2" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                  {step.number}
                </div>
                <h3 className="text-base font-bold text-gray-900 mb-2">{step.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Advantages */}
      <section className="py-14 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="text-xs font-bold uppercase tracking-widest text-[#00a340] mb-2 block">Pourquoi nous choisir</span>
            <h2
              className="text-2xl md:text-4xl font-extrabold text-gray-900 mb-8 leading-tight"
              style={{ fontFamily: "'Montserrat', sans-serif" }}
            >
              Les avantages{" "}
              <span className="italic text-[#00a340]">KS BTP</span>
            </h2>
            <ul className="flex flex-col gap-4">
              {advantages.map((adv) => (
                <li key={adv} className="flex items-start gap-3">
                  <div className="w-5 h-5 flex items-center justify-center rounded-full bg-[#00a340] flex-shrink-0 mt-0.5">
                    <i className="ri-check-line text-white text-xs"></i>
                  </div>
                  <span className="text-gray-700 text-sm leading-relaxed">{adv}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-[#0d2b18] rounded-2xl p-8 md:p-10 text-white">
            <h3 className="text-xl font-bold mb-2" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              Besoin d&apos;un devis ?
            </h3>
            <p className="text-gray-300 text-sm mb-6 leading-relaxed">
              Contactez-nous pour une étude personnalisée de votre projet. Réponse sous 48h.
            </p>
            <div className="flex flex-col gap-3 mb-6">
              <div className="flex items-center gap-3 text-sm text-gray-300">
                <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#00a340]/20 text-[#4cd97a]">
                  <i className="ri-phone-line"></i>
                </div>
                <span>Réponse rapide garantie</span>
              </div>
              <div className="flex items-center gap-3 text-sm text-gray-300">
                <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#00a340]/20 text-[#4cd97a]">
                  <i className="ri-shield-check-line"></i>
                </div>
                <span>Garantie décennale incluse</span>
              </div>
              <div className="flex items-center gap-3 text-sm text-gray-300">
                <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#00a340]/20 text-[#4cd97a]">
                  <i className="ri-map-pin-line"></i>
                </div>
                <span>Intervention Île-de-France</span>
              </div>
            </div>
            <a
              href="/#contact"
              className="block w-full text-center px-6 py-3 rounded-md bg-[#00a340] text-white font-semibold text-sm whitespace-nowrap hover:bg-[#007a30] transition-colors cursor-pointer"
            >
              Nous contacter
            </a>
          </div>
        </div>
      </section>

      {/* Related services */}
      <section className="py-12 md:py-16 bg-[#f8fdf9] border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <h3
            className="text-lg font-bold text-gray-900 mb-6"
            style={{ fontFamily: "'Montserrat', sans-serif" }}
          >
            Nos autres domaines d&apos;expertise
          </h3>
          <div className="flex flex-wrap gap-3">
            {relatedServices.map((s) => (
              <Link
                key={s.label}
                to={s.href}
                className="px-4 py-2 rounded-full border border-gray-200 text-gray-700 text-sm font-medium hover:border-[#00a340] hover:text-[#00a340] hover:bg-[#f0fdf4] transition-colors cursor-pointer whitespace-nowrap"
              >
                {s.label}
              </Link>
            ))}
          </div>
        </div>
      </section>
    </main>
    <Footer />
    </div>
  );
}
